import { isEmpty } from "lodash-es";
import z from "zod/v4";

import type { ISO8601_DATE, ISO8601_TIME } from "../../../utils/iso8601";
import type { SerialisedLexicalEditorDesign } from "../../message/editor/types/serialised-editor-design";
import type { FollowUp } from "../server/follow-up-service";

const followUpReason: z.ZodType<FollowUp["config"]> = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("no_interaction"),
    source_id: z.string(),
  }),
  z.object({
    type: z.literal("no_survey_completion"),
    survey_id: z.string(),
    source_id: z.string(),
  }),
  z.object({
    type: z.literal("no_link_click"),
    source_id: z.string(),
    hyperlink_url: z.string().nullable(),
  }),
  z.object({
    type: z.literal("link_click"),
    source_id: z.string(),
    hyperlink_url: z.string().nullable(),
  }),
]);

const immediateSchedule = z.object({
  type: z.literal("immediate"),
});

const specificTimeSchedule = z
  .object({
    type: z.literal("specific"),
    date: z.iso
      .date()
      .nullable()
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      .transform((val) => val as unknown as ISO8601_DATE | null),
    time: z.iso
      .time()
      .nullable()
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      .transform((val) => val as unknown as ISO8601_TIME | null),
    timezone: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    // We use a superRefine pattern so that we out inferred types are allowed to be null
    // when setting up default fields, but we still enforce that they are required
    // when validating the final schema.
    if (data.date === null) {
      ctx.addIssue({ path: ["date"], message: "Date is required", code: "custom" });
    }

    if (data.time === null) {
      ctx.addIssue({ path: ["time"], message: "Time is required", code: "custom" });
    }
  });

const scheduledTimeSchedule = z
  .object({
    type: z.literal("scheduled"),
    amount: z.number().min(1, "Amount is required").nullable(),
    period: z.enum(["days", "weeks", "months", "years"]).nullable(),
    time: z.iso
      .time()
      .nullable()
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      .transform((val) => val as unknown as ISO8601_TIME | null),
    timezone: z.string().nullable(),
    allow_weekends: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // We use a superRefine pattern so that we out inferred types are allowed to be null
    // when setting up default fields, but we still enforce that they are required
    // when validating the final schema.
    if (data.amount === null) {
      ctx.addIssue({ path: ["amount"], message: "Amount is required", code: "custom" });
    }

    if (data.period === null) {
      ctx.addIssue({ path: ["period"], message: "Period is required", code: "custom" });
    }

    if (data.time === null) {
      ctx.addIssue({ path: ["time"], message: "Time is required", code: "custom" });
    }
  });

const baseFollowUpFields = z.object({
  title: z.string().min(1, "Title is required"),
  reason: followUpReason.superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({ message: "Reason is required", code: "custom" });
    }
  }),
  // Hacky way to allow an arbitrary object but still have types
  design: z
    .looseObject({})
    .nullable()
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    .transform((val) => val as unknown as SerialisedLexicalEditorDesign | null)
    .superRefine((data, ctx) => {
      if (isEmpty(data)) {
        ctx.addIssue({ message: "Design must be set", code: "custom" });
      }
    }),
});

const slackFollowUpFields = z.object({
  channel: z.literal("slack"),
  channelData: z.object({
    slack_alias_id: z.string().nullish(),
  }),
});

const teamsFollowUpFields = z.object({
  channel: z.literal("teams"),
  channelData: z.undefined(),
});

const textFollowUpFields = z.object({
  channel: z.enum(["twilio", "whatsapp"]),
  channelData: z.undefined(),
});

const emailFollowUpFields = z.object({
  channel: z.enum(["work_email", "personal_email"]),
  channelData: z.object({
    email_sender: z.string().min(1, "Email sender is required"),
    email_subject: z.string().min(1, "Email subject is required"),
    email_design_id: z
      .string()
      .nullable()
      .superRefine((data, ctx) => {
        if (data === null || data === "") {
          ctx.addIssue({ message: "Email design must be selected", code: "custom" });
        }
      }),
  }),
});

const channelWithDataFields = z.discriminatedUnion("channel", [
  slackFollowUpFields,
  teamsFollowUpFields,
  textFollowUpFields,
  emailFollowUpFields,
]);

export type FollowUpChannelWithData = z.infer<typeof channelWithDataFields>;

const finishedFollowUpSchema = z.object({
  mode: z.literal("finished"),
  schedule: z.discriminatedUnion("type", [immediateSchedule, specificTimeSchedule]),
});
const scheduledFollowUpSchema = z.object({
  mode: z.literal("scheduled"),
  schedule: scheduledTimeSchedule,
});

const followUpModeSchema = z.union([finishedFollowUpSchema, scheduledFollowUpSchema]);

export type FollowUpModeSchemaType = z.infer<typeof followUpModeSchema>;

export const followUpSchema = followUpModeSchema.and(baseFollowUpFields).and(channelWithDataFields);

const emailFollowUpFieldsNewsletter = z.object({
  channel: z.enum(["work_email", "personal_email"]),
  channelData: z.object({
    email_sender: z.string().min(1, "Email sender is required"),
    email_subject: z.string().min(1, "Email subject is required"),
    email_design_id: z.string().nullable(), // Not required for newsletter
  }),
});

const channelWithDataFieldsNewsletter = z.discriminatedUnion("channel", [
  slackFollowUpFields,
  teamsFollowUpFields,
  textFollowUpFields,
  emailFollowUpFieldsNewsletter,
]);

const baseFollowUpFieldsNewsletter = z.object({
  title: z.string().min(1, "Title is required"),
  reason: followUpReason.superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({ message: "Reason is required", code: "custom" });
    }
  }),
  design: z
    .looseObject({})
    .nullable()
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    .transform((val) => val as unknown as SerialisedLexicalEditorDesign | null),
});

export const followUpSchemaNewsletter = followUpModeSchema
  .and(baseFollowUpFieldsNewsletter)
  .and(channelWithDataFieldsNewsletter);

export type FollowUpSchemaType = z.infer<typeof followUpSchema>;
